import { ReactElement, RefObject, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef } from 'react';
import { DEFAULT_PAGE_SIZE, partTestId } from '@connect-x-app/shared-utils';
import { useVirtualizer } from '@tanstack/react-virtual';

import './ScrollList.scss';

export type ScrollListRef = {
  scrollToBottom: () => void;
  scrollToIndex: (index: number) => void;
};

type Props<T> = {
  attachmentPreviewComponent?: ReactElement | null;
  data: T[];
  defaultSize?: number;
  initialIndex: number;
  inverted?: boolean;
  isFetching?: boolean;
  // Set when a focus/unread target exists and the consumer drives the landing imperatively via
  // scrollToIndex. It stops the list from auto re-centering on every initialIndex change (which would
  // yank the viewport as newer pages / incoming messages shift the target's index).
  landOnInitialIndex?: boolean;
  listEmptyComponent?: ReactElement;
  onEndReached?: () => void;
  onStartReached?: () => void;
  onViewableItemsChanged?: (indices: number[]) => void;
  overscan?: number;
  pageSize?: number;
  ref?: RefObject<ScrollListRef | null>;
  renderItem: (item: T, index: number) => ReactElement;
  testId?: string;
};

const START_END_THRESHOLD_PX = 100;
const PIN_THRESHOLD_PX = 2;

export const ScrollList = <T,>({
  attachmentPreviewComponent,
  data,
  defaultSize = 70,
  initialIndex,
  inverted = false,
  isFetching,
  landOnInitialIndex = false,
  listEmptyComponent,
  onEndReached,
  onStartReached,
  onViewableItemsChanged,
  overscan = 25,
  pageSize = DEFAULT_PAGE_SIZE,
  ref,
  renderItem,
  testId,
}: Props<T>) => {
  // The scrollable element for your list
  const parentRef = useRef<HTMLDivElement>(null);
  const prevDataLengthRef = useRef<number>(data.length);
  const atBottomRef = useRef<boolean>(true);
  const atTopRef = useRef<boolean>(false);
  const initialBottomDoneRef = useRef<boolean>(false);
  const pinnedToBottomRef = useRef<boolean>(true);
  const lastScrollTopRef = useRef<number>(0);
  const prevInitialIndexRef = useRef<number | undefined>(undefined);
  const prevVisibleIndicesRef = useRef<string>('');
  const smoothScrollActiveRef = useRef<boolean>(false);
  const rowVirtualizer = useVirtualizer({
    count: data.length,
    estimateSize: () => defaultSize,
    getScrollElement: () => parentRef.current,
    overscan,
  });
  const items = rowVirtualizer.getVirtualItems();

  useEffect(() => {
    const el = parentRef.current;
    if (!el || !onViewableItemsChanged) {
      return;
    }

    const checkVisibility = () => {
      if (items.length === 0) {
        return;
      }

      const scrollTop = el.scrollTop;
      const clientHeight = el.clientHeight;
      const scrollBottom = scrollTop + clientHeight;

      const visibleIndices = items
        .filter(item => {
          const itemStart = item.start;
          const itemEnd = item.start + item.size;
          // Strictly visible: item is entirely within the container's scroll boundaries
          // We use a small epsilon (1px) to avoid precision issues with scroll positions
          return itemStart >= scrollTop - 1 && itemEnd <= scrollBottom + 1;
        })
        .map(item => (inverted ? data.length - 1 - item.index : item.index));

      const visibleIndicesKey = visibleIndices.join(',');
      if (visibleIndicesKey !== prevVisibleIndicesRef.current) {
        prevVisibleIndicesRef.current = visibleIndicesKey;
        onViewableItemsChanged(visibleIndices);
      }
    };

    checkVisibility();

    el.addEventListener('scroll', checkVisibility, { passive: true });
    return () => el.removeEventListener('scroll', checkVisibility);
  }, [items, onViewableItemsChanged, inverted, data.length]);

  const ensureBottom = useCallback((el: HTMLElement, smooth = false) => {
    let isSmooth = smooth;
    const currentScrollTop = el.scrollTop;
    const maxScrollTop = Math.max(0, Math.ceil(el.scrollHeight - el.clientHeight));
    const jumpDistance = maxScrollTop - currentScrollTop;

    // If the jump to the bottom is too big do not animate since the virtualizer cannot keep up
    if (isSmooth && jumpDistance > el.clientHeight * 3) {
      isSmooth = false;
    }

    if (isSmooth) {
      smoothScrollActiveRef.current = true;
      el.scrollTo({
        behavior: 'smooth',
        top: maxScrollTop,
      });
    } else {
      if (smoothScrollActiveRef.current) {
        return;
      }

      const doInstantScroll = () => {
        el.scrollTop = Math.max(0, Math.ceil(el.scrollHeight - el.clientHeight));
      };

      // Called 3 times to be robust and make it work (if we don't do this it will not scroll all the way to the bottom)
      // First call to already be sure it is at least called once
      doInstantScroll();
      requestAnimationFrame(() => {
        // called a second time in the next repaint cycle, for when some calculations from the previous one have been done
        // Also to be more reliable than the setTimeout one
        doInstantScroll();
        requestAnimationFrame(() => {
          // This will run after the next cycle to make it really up to date (less reliable)
          setTimeout(doInstantScroll, 0);
        });
      });
    }
  }, []);

  // Method to get the inverted index if needed (to invert the list)
  const getCorrectIndex = useCallback(
    (index: number) => {
      return inverted ? data.length - 1 - index : index;
    },
    [data.length, inverted],
  );

  useImperativeHandle(
    ref,
    () => ({
      scrollToBottom: () => {
        const el = parentRef.current;
        if (el) {
          pinnedToBottomRef.current = true;
          ensureBottom(el, true);
          lastScrollTopRef.current = el.scrollTop;
        }
      },
      scrollToIndex: (index: number) => {
        const el = parentRef.current;
        if (el) {
          pinnedToBottomRef.current = false;
          atBottomRef.current = false;
          rowVirtualizer.scrollToIndex(getCorrectIndex(index), { align: 'center' });
          lastScrollTopRef.current = el.scrollTop;
        }
      },
    }),
    [ensureBottom, getCorrectIndex, rowVirtualizer],
  );

  // Fetch more recent messages if list is smaller than total view height because onStart/EndReached is not fired
  const checkContentHeight = useCallback(() => {
    const el = parentRef.current;

    if (!el || isFetching || rowVirtualizer.getVirtualItems().length === 0) {
      return;
    }

    const contentHeight = rowVirtualizer.getTotalSize();
    const clientHeight = el.clientHeight;
    if (atTopRef.current && atBottomRef.current && contentHeight < clientHeight) {
      requestAnimationFrame(() => {
        if (inverted) {
          onEndReached?.();
        } else {
          onStartReached?.();
        }
      });
    }
  }, [isFetching, inverted, onStartReached, onEndReached, rowVirtualizer]);

  const scrollToInitialPosition = useCallback(
    (el: HTMLDivElement) => {
      if (initialIndex > 0) {
        // Scroll to the initial index if there is one
        rowVirtualizer.scrollToIndex(getCorrectIndex(initialIndex), { align: 'center' });
        pinnedToBottomRef.current = false;
        atBottomRef.current = false;
      } else {
        // No initial index yet — scroll to (and pin) the bottom. Keeping atBottom true here is what
        // lets checkContentHeight/onEndReached fetch the newer messages when a focus/unread target is
        // still the bottom item; the consumer then lands on it via the imperative scrollToIndex.
        ensureBottom(el);
        pinnedToBottomRef.current = true;
        atBottomRef.current = true;
      }
    },
    [ensureBottom, getCorrectIndex, initialIndex, rowVirtualizer],
  );

  // Set scroll to the initial index and kill this effect once the initial scroll is done
  useLayoutEffect(() => {
    const el = parentRef.current;

    if (!el || data.length === 0 || items.length === 0) {
      return;
    }

    // If initial scrolling is done, stop!
    if (initialBottomDoneRef.current) {
      return;
    }

    scrollToInitialPosition(el);
    // Kill this effect once the initial scroll is done
    initialBottomDoneRef.current = true;
    prevInitialIndexRef.current = initialIndex;

    // Update refs for the scroll-handler
    atTopRef.current = el.scrollTop <= START_END_THRESHOLD_PX;
    lastScrollTopRef.current = el.scrollTop;
  }, [data.length, initialIndex, items.length, scrollToInitialPosition]);

  useLayoutEffect(() => {
    const el = parentRef.current;

    if (!el || !initialBottomDoneRef.current || data.length === 0 || initialIndex === prevInitialIndexRef.current) {
      return;
    }

    if (landOnInitialIndex) {
      // The consumer drives the landing imperatively (scrollToIndex) once the surrounding messages
      // have loaded. Skip auto re-scrolling here so the viewport is not re-centered (yanked) on
      // every index change caused by a newer page or an incoming message.
      prevInitialIndexRef.current = initialIndex;
      return;
    }

    const dataLengthChanged = data.length !== prevDataLengthRef.current;

    if (pinnedToBottomRef.current && dataLengthChanged) {
      prevInitialIndexRef.current = initialIndex;
      return;
    }

    scrollToInitialPosition(el);

    prevInitialIndexRef.current = initialIndex;
  }, [data.length, initialIndex, landOnInitialIndex, scrollToInitialPosition]);

  // Fire start/end reached based on scroll position and track top/bottom --> this is used to fetch older or newer data
  useEffect(() => {
    const el = parentRef.current;
    if (!el) {
      return;
    }

    const handle = () => {
      if (isFetching) {
        return;
      }
      const { clientHeight, scrollHeight } = el;
      const scrollTop = el.scrollTop;
      const atTop = scrollTop <= START_END_THRESHOLD_PX;
      const atBottom = scrollTop + clientHeight >= scrollHeight - START_END_THRESHOLD_PX;

      // Track direction and strict pin state
      const last = lastScrollTopRef.current;
      const delta = scrollTop - last;
      const maxScrollTop = Math.max(0, scrollHeight - clientHeight);
      const nearStrictBottom = maxScrollTop - scrollTop <= PIN_THRESHOLD_PX;

      // The smooth scroll-to-bottom is done once it lands (near strict bottom) or the user
      // interrupts it by scrolling up.
      if (smoothScrollActiveRef.current && (delta < 0 || nearStrictBottom)) {
        smoothScrollActiveRef.current = false;
      }

      if (delta < 0) {
        // User scrolled up: unpin
        pinnedToBottomRef.current = false;
      } else if (nearStrictBottom) {
        // At strict bottom: pin
        pinnedToBottomRef.current = true;
      }
      lastScrollTopRef.current = scrollTop;

      atTopRef.current = atTop;
      atBottomRef.current = atBottom;

      if (atTop) {
        if (inverted) {
          onStartReached?.();
        } else {
          onEndReached?.();
        }
      } else if (atBottom) {
        if (inverted) {
          onEndReached?.();
        } else {
          onStartReached?.();
        }
      }
    };

    el.addEventListener('scroll', handle, { passive: true });
    return () => el.removeEventListener('scroll', handle);
  }, [isFetching, inverted, onEndReached, onStartReached, rowVirtualizer]);

  useEffect(() => {
    checkContentHeight();
  }, [checkContentHeight]);

  useLayoutEffect(() => {
    const el = parentRef.current;
    if (!el) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      checkContentHeight();
    });

    resizeObserver.observe(el);

    return () => resizeObserver.disconnect();
  }, [checkContentHeight]);

  // Maintain scroll position roughly when data length changes (simple heuristic)
  useLayoutEffect(() => {
    const el = parentRef.current;
    if (!el) {
      return;
    }

    const prevLen = prevDataLengthRef.current;
    const currLen = data.length;
    if (currLen > prevLen) {
      // If new items likely appended at the top (inverted true) or bottom (inverted false), try to keep viewport stable
      const added = currLen - prevLen;
      // Adjust by estimated size (better than a jump to top)
      const delta = added * defaultSize;
      if (atTopRef.current && !atBottomRef.current) {
        el.scrollTop += delta;
      }
      if (!inverted) {
        // Non-inverted: appends appear at visual bottom.
        // If pinned to bottom, keep pinned; otherwise don't move the viewport.
        if (pinnedToBottomRef.current && added < pageSize) {
          // Scroll to bottom
          ensureBottom(el);
        }
      }
      // Generic bottom pin: if currently pinned, ensure we remain at the bottom when receiving a message
      if (pinnedToBottomRef.current && added < pageSize) {
        ensureBottom(el, true);
      }
    }
    prevDataLengthRef.current = currLen;
  }, [data.length, defaultSize, inverted, ensureBottom, pageSize]);

  const content = useMemo(() => {
    if (data.length <= 0) {
      return <div className="list-empty-container">{listEmptyComponent}</div>;
    }

    return (
      <div className="main-scroll-items-container" style={{ height: rowVirtualizer.getTotalSize() }}>
        <div
          className="sub-scroll-items-container"
          style={{
            transform: `translateY(${items[0]?.start ?? 0}px)`,
          }}
        >
          {items.map(virtualRow => {
            const correctedIndex = getCorrectIndex(virtualRow.index);
            const child = renderItem(data[correctedIndex], correctedIndex);

            return (
              <div className="scroll-item" data-index={virtualRow.index} key={virtualRow.key} ref={rowVirtualizer.measureElement}>
                {child}
              </div>
            );
          })}
        </div>
      </div>
    );
  }, [data, getCorrectIndex, items, listEmptyComponent, renderItem, rowVirtualizer]);

  return (
    <div className="scroll-list-container-wrapper">
      {attachmentPreviewComponent ? (
        <div className="attachment-upload-preview-container">{attachmentPreviewComponent}</div>
      ) : null}

      <div
        className="scroll-list-container"
        data-test={testId}
        ref={parentRef}
        style={{
          visibility: attachmentPreviewComponent ? 'hidden' : 'visible',
        }}
      >
        {content}
        {isFetching ? <div className="scroll-list-loading" data-test={partTestId(testId, 'loading')} /> : null}
      </div>
    </div>
  );
};
